import { useTranslation } from 'react-i18next';
import { SupportSparkle } from './desk/desk-sidebar';

/**
 * De grote supportkaart onderaan de rechterkolom (replica-spec §10.3).
 *
 * De kleine zus hiervan staat in de zijbalk (§4.4); dit is dezelfde kaart op het formaat van de
 * kolom. Twee componenten en niet één met een `size`-vlag: de twee verschillen in bijna elke maat
 * — padding, de diameter van de cirkels, de plaats van de badge — en een component met twee van
 * alles leest slechter dan twee componenten met één van alles.
 *
 * **Statisch, en dat is een keuze.** Er is geen supportmodule en geen ticketsysteem; wat hier staat
 * is een adres uit de configuratie (`VITE_SUPPORT_URL`). Ontbreekt dat adres, dan is er geen kaart —
 * en niet een knop die nergens heen gaat. De belofte "binnen het uur op werkdagen" uit de mockup
 * staat er daarom ook niet: dat is een toezegging die wij niet kunnen nakomen en die een klant
 * anders op zijn eigen scherm aan zijn mensen doet.
 */
export function AppSupportCard() {
  const { t } = useTranslation();
  const href = import.meta.env.VITE_SUPPORT_URL ?? null;
  if (!href) return null;

  return (
    <div
      data-testid="support-card"
      className="relative overflow-hidden rounded-xl bg-gradient-to-br from-brand-primary to-brand-cyan px-[22px] pb-[22px] pt-8 text-center shadow-md"
    >
      {/* De twee decoratieve cirkels, met de maten en de doorzichtigheid uit §10.3. Ze liggen
          deels buiten de kaart; `overflow-hidden` snijdt ze af. */}
      <span
        aria-hidden
        className="absolute -left-[30px] -top-[60px] h-[200px] w-[200px] rounded-full bg-[rgba(255,255,255,0.08)]"
      />
      <span
        aria-hidden
        className="absolute -bottom-20 -right-[50px] h-[220px] w-[220px] rounded-full bg-[rgba(255,255,255,0.06)]"
      />

      {/* De oranje badge valt aan de bovenkant 12 px weg door `overflow-hidden`. Dat staat zo in de
          mockup en de spec zegt uitdrukkelijk het zo over te nemen. */}
      <span
        aria-hidden
        className="absolute -top-3 left-[22px] z-[2] flex h-10 w-10 items-center justify-center rounded-pill bg-brand-orange text-text-on-dark shadow-[0_6px_14px_rgba(233,141,78,0.4)]"
      >
        <SupportSparkle />
      </span>

      <span
        aria-hidden
        className="relative mx-auto mb-3.5 flex h-11 w-11 items-center justify-center rounded-md bg-surface text-brand-primary shadow-[0_4px_12px_rgba(0,0,0,0.1)]"
      >
        <SupportSparkle size={20} />
      </span>

      <p className="relative mb-1 font-heading text-title-md font-semibold text-text-on-dark">
        {t('support.title')}
      </p>
      <p className="relative mb-4 text-small leading-[1.5] text-[rgba(255,255,255,0.9)]">
        {t('support.body')}
      </p>
      <a
        href={href}
        className="relative inline-block rounded-pill bg-surface px-[26px] py-2.5 font-heading text-body font-semibold text-brand-primary shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(0,0,0,0.15)]"
      >
        {t('support.button')}
      </a>
    </div>
  );
}
